import { useEffect, useState } from "react"
import { useApi } from "../hooks/useApi"
import { useProfile } from "../hooks/useProfile"
import { useToast } from "../context/toasts/useToast"
import { formatRelativeTime, formatDateTime } from "../utils/dateFormat"
import UserIcon from "../components/icons/UserIcon"
import ShieldIcon from "../components/icons/ShieldIcon"
import HistoryIcon from "../components/icons/HistoryIcon"

interface UserProfile {
  id: number
  email: string
  username: string
  first_name?: string
  last_name?: string
  is_active: boolean
  is_admin: boolean
  is_locked: boolean
  failed_login_attempts: number
  last_login_at?: string
  created_at?: string
}

function Profile() {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Change password form state
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  const { getCurrentUser } = useApi()
  const { fetchLoginHistory, changePassword, loginHistory } = useProfile()
  const { addNotification } = useToast()

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true)
        const data = await getCurrentUser()
        setProfile(data as unknown as UserProfile)
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Failed to load profile"
        setError(msg)
        addNotification({
          type: "error",
          title: "Error",
          message: msg,
          duration: 5000,
        })
      } finally {
        setLoading(false)
      }
    }
    loadProfile()
  }, [getCurrentUser, addNotification])

  const handleToggleHistory = () => {
    if (!showHistory) {
      fetchLoginHistory()
    }
    setShowHistory(!showHistory)
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      addNotification({
        type: "error",
        title: "Validation Error",
        message: "New passwords do not match.",
        duration: 5000,
      })
      return
    }

    if (newPassword.length < 6) {
      addNotification({
        type: "error",
        title: "Validation Error",
        message: "Password must be at least 6 characters.",
        duration: 5000,
      })
      return
    }

    const success = await changePassword({
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    })

    if (success) {
      setCurrentPassword("")
      setNewPassword("")
      setConfirmPassword("")
      setShowPasswordForm(false)
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-gray-400 text-center py-12">Loading profile...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-gray-400 text-center py-12">Profile not found.</div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 max-w-2xl space-y-8 text-gray-100">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <span className="w-8 h-8">
          <UserIcon />
        </span>
        <h1 className="text-3xl font-bold">Profile</h1>
      </div>

      {/* User Info Card */}
      <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <span className="w-5 h-5">
            <UserIcon />
          </span>
          Account Information
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <span className="text-gray-400 text-sm">Username</span>
            <p className="text-white font-medium">{profile.username}</p>
          </div>
          <div>
            <span className="text-gray-400 text-sm">Email</span>
            <p className="text-white font-medium">{profile.email}</p>
          </div>
          <div>
            <span className="text-gray-400 text-sm">Name</span>
            <p className="text-white font-medium">
              {[profile.first_name, profile.last_name].filter(Boolean).join(" ") || "—"}
            </p>
          </div>
          <div>
            <span className="text-gray-400 text-sm">Member Since</span>
            <p className="text-white font-medium">
              {profile.created_at ? formatDateTime(profile.created_at) : "—"}
            </p>
          </div>
          <div>
            <span className="text-gray-400 text-sm">Last Login</span>
            <p className="text-white font-medium">
              {profile.last_login_at ? formatRelativeTime(profile.last_login_at) : "—"}
            </p>
          </div>
          <div>
            <span className="text-gray-400 text-sm">Account Status</span>
            <p className={`font-medium ${profile.is_active && !profile.is_locked ? "text-green-400" : "text-red-400"}`}>
              {profile.is_active && !profile.is_locked ? "Active" : profile.is_locked ? "Locked" : "Inactive"}
            </p>
          </div>
        </div>

        {profile.failed_login_attempts > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <span className="text-gray-400 text-sm">Failed Login Attempts</span>
            <p className="text-yellow-400 font-medium">{profile.failed_login_attempts}</p>
          </div>
        )}
      </div>

      {/* Change Password Section */}
      <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
        <button
          onClick={() => setShowPasswordForm(!showPasswordForm)}
          className="w-full flex items-center justify-between"
        >
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <span className="w-5 h-5">
              <ShieldIcon />
            </span>
            Change Password
          </h2>
          <span className={`transform transition-transform ${showPasswordForm ? "rotate-180" : ""}`}>
            ▼
          </span>
        </button>

        {showPasswordForm && (
          <form onSubmit={handleChangePassword} className="mt-6 space-y-4">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-300 mb-2">
                Current Password
              </label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-2">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {newPassword !== confirmPassword && confirmPassword && (
              <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded text-sm">
                Passwords do not match
              </div>
            )}

            <button
              type="submit"
              disabled={newPassword !== confirmPassword || newPassword.length < 6 || !currentPassword}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Update Password
            </button>
          </form>
        )}
      </div>

      {/* Login History Section */}
      <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
        <button
          onClick={handleToggleHistory}
          className="w-full flex items-center justify-between"
        >
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <span className="w-5 h-5">
              <HistoryIcon />
            </span>
            Login History
          </h2>
          <span className={`transform transition-transform ${showHistory ? "rotate-180" : ""}`}>
            ▼
          </span>
        </button>

        {showHistory && (
          <div className="mt-4">
            {loginHistory.length === 0 ? (
              <p className="text-gray-400 text-center py-4">No login history found.</p>
            ) : (
              <div className="space-y-2">
                {loginHistory.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-3 bg-gray-700 rounded"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          entry.success ? "bg-green-400" : "bg-red-400"
                        }`}
                      />
                      <div>
                        <p className="text-sm text-gray-200">
                          {entry.success ? "Successful login" : "Failed login"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {entry.ip_address} — {entry.user_agent?.slice(0, 60)}
                          {entry.user_agent?.length > 60 ? "..." : ""}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap ml-4">
                      {formatRelativeTime(entry.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Profile